import React, { useState, type UIEvent } from 'react'
import {
  Button,
  Card,
  DialogOverlay,
  DialogOverlayActions,
  H2,
  Paragraph,
  Radio,
  Row,
  Separator,
  useOverlay,
  Skeleton,
  useContainerWidth,
} from '@fs/zion-ui'
import { useGroupsContext } from '@fs/zion-tree-groups'
import axios from '@fs/zion-axios'
import { useTranslation } from 'react-i18next'
import PrivacyNoticeOverlay from './PrivacyNoticeOverlay'

export default function PrivacyContent(): JSX.Element {
  const { treeDetails, refetchTreeDetails } = useGroupsContext()
  const treeId = treeDetails?.treeId
  const currentPrivate = treeDetails?.private
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, md: false })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const noticeOverlay = useOverlay()
  const errorOverlay = useOverlay()

  // Track which option is being viewed in the dialog ('private' or 'public')
  const [dialogOption, setDialogOption] = useState<'private' | 'public' | null>(null)

  // Current privacy value from tree endpoint
  const isCurrentlyPrivate = currentPrivate ?? false

  // True when dialog option differs from current privacy setting (user wants to change, not just view info)
  const isChangingSetting = dialogOption !== null && (dialogOption === 'private') !== isCurrentlyPrivate

  const openDialog = (option: 'private' | 'public', event: UIEvent): void => {
    setDialogOption(option)
    noticeOverlay.handleClick(event)
  }

  const closeDialog = (): void => {
    setDialogOption(null)
    noticeOverlay.close()
  }

  const handleSave = (agreementChecked: boolean): void => {
    if (!treeId || !dialogOption) return
    setIsSubmitting(true)
    const isPrivate = dialogOption === 'private'
    axios
      .post(`/service/tree/tree-data/groups/trees/${treeId}`, {
        private: isPrivate,
        ...(isPrivate && agreementChecked ? { autoPublicAccepted: true } : {}),
      })
      .then(() => {
        refetchTreeDetails?.()
        closeDialog()
        return undefined
      })
      .catch((error) => {
        // If queue is full or the tree is in process of being changed, 423 status is returned
        if (error.response?.status === 423) {
          closeDialog()
          errorOverlay.open()
        } else {
          console.error('Error saving privacy setting:', error)
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <>
      <Card>
        <H2 size="H5">{t('group-management.privacy-setting.heading')}</H2>
        <Separator size="md" />
        <Row wrap={isMobile}>
          {/* Public Card */}
          <Card edges="border">
            <Radio
              data-testid="privacy-setting-public"
              label={t('group-management.privacy-setting.public-card.title')}
              name="privacy"
              value="public"
              checked={!isCurrentlyPrivate}
              onChange={(event) => {
                // Only open dialog if changing from private to public
                if (isCurrentlyPrivate) {
                  openDialog('public', event)
                }
              }}
            />
            <Paragraph size="sm">{t('group-management.privacy-setting.public-card.description')}</Paragraph>
            <Separator size="xs" />
            <Button data-testid="privacy-learn-more-public" onClick={(event) => openDialog('public', event)}>
              {t('tree-import.gedcom-info-dialog.learn-more')}
            </Button>
          </Card>

          {/* Private Card */}
          <Card edges="border">
            <Radio
              data-testid="privacy-setting-private"
              label={t('group-management.privacy-setting.private-card.title')}
              name="privacy"
              value="private"
              checked={isCurrentlyPrivate}
              onChange={(event) => {
                // Only open dialog if changing from public to private
                if (!isCurrentlyPrivate) {
                  openDialog('private', event)
                }
              }}
            />
            <Paragraph size="sm">{t('group-management.privacy-setting.private-card.description')}</Paragraph>
            <Separator size="xs" />
            <Button data-testid="privacy-learn-more-private" onClick={(event) => openDialog('private', event)}>
              {t('tree-import.gedcom-info-dialog.learn-more')}
            </Button>
          </Card>
        </Row>
        <Separator size="md" />
      </Card>
      {(noticeOverlay.isOpen || noticeOverlay.transitioning) && (
        <PrivacyNoticeOverlay
          overlay={noticeOverlay}
          isPrivate={dialogOption === 'private'}
          isChangingSetting={isChangingSetting}
          onSave={handleSave}
          onCancel={closeDialog}
          isSubmitting={isSubmitting}
          isCurrentlyPrivate={isCurrentlyPrivate}
        />
      )}
      {(errorOverlay.isOpen || errorOverlay.transitioning) && (
        <DialogOverlay
          title={t('group-management.privacy-setting.processing-failed.title')}
          footer={
            <DialogOverlayActions>
              <Button data-testid="privacy-error-ok-button" onClick={errorOverlay.close} emphasis="high">
                {t('group-management.manage-contributors.permissions-action')}
              </Button>
            </DialogOverlayActions>
          }
          {...errorOverlay}
        >
          {t('group-management.privacy-setting.processing-failed.description')}
        </DialogOverlay>
      )}
    </>
  )
}

export function PrivacyContentSkeleton(): JSX.Element {
  return (
    <Card>
      <Skeleton.H3 />
      <Separator size="md" />
      <Row>
        <Card edges="border" margins="xs">
          <Skeleton.Paragraph maxWidth={100} />
          <Skeleton.Paragraph maxWidth={400} lineCount={3} />
          {/* eslint-disable-next-line test-selectors/button -- NA */}
          <Skeleton.Button />
        </Card>
        <Card edges="border" margins="xxs">
          <Skeleton.Paragraph maxWidth={100} />
          <Skeleton.Paragraph maxWidth={400} lineCount={3} />
          {/* eslint-disable-next-line test-selectors/button -- NA */}
          <Skeleton.Button />
        </Card>
      </Row>
      <Separator size="md" />
    </Card>
  )
}
